import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import type { UserManager } from 'oidc-client-ts';
import { createApi, ApiError } from '../api/client';
import type { AppConfig } from '../config';
import type { UserItem } from '../types';
import { Spinner } from '../components/primitives';
import { AdminContext } from './context';

/**
 * Guards the admin area: redirects to Cognito when signed out, then loads
 * the caller's user record (role) from the API — which auto-provisions a
 * read-only viewer account on first sign-in.
 */
export function AuthGate({
  config,
  userManager,
  children,
}: {
  config: AppConfig;
  userManager: UserManager;
  children: ReactNode;
}) {
  const auth = useAuth();
  const api = useMemo(() => createApi(config, userManager), [config, userManager]);
  const [me, setMe] = useState<UserItem | null>(null);
  const [meError, setMeError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error) {
      void auth.signinRedirect();
    }
  }, [auth]);

  const loadMe = async () => {
    try {
      const { user } = await api.getMe();
      setMe(user);
      setMeError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        void auth.signinRedirect();
        return;
      }
      setMeError(err instanceof Error ? err.message : 'Could not load your account');
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated) void loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  if (auth.error) {
    return (
      <FullPageMessage
        title="Sign-in failed"
        body={auth.error.message}
        actionLabel="Try again"
        onAction={() => void auth.signinRedirect()}
      />
    );
  }

  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Redirecting to sign-in" />
      </div>
    );
  }

  if (meError) {
    return (
      <FullPageMessage
        title="Couldn't load your account"
        body={meError}
        actionLabel="Retry"
        onAction={() => void loadMe()}
      />
    );
  }

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading your account" />
      </div>
    );
  }

  return (
    <AdminContext.Provider value={{ api, me, refreshMe: loadMe }}>{children}</AdminContext.Provider>
  );
}

function FullPageMessage({
  title,
  body,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <h1 className="text-xl font-bold">{title}</h1>
      <p className="mt-2 max-w-md text-sm text-muted">{body}</p>
      <button
        type="button"
        onClick={onAction}
        className="mt-6 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-strong"
      >
        {actionLabel}
      </button>
    </div>
  );
}
